import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { CheckCircle2, TrendingUp } from 'lucide-react';
import {
  tariefContractjaarApi,
  TARIEF_VOORSTELLEN_KEY,
  type TariefVoorstel,
} from '../../services/tariefContractjaarApi';

const euro = (n: number | null | undefined) =>
  new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(Number(n ?? 0));

const KNOP_BASIS = 'inline-flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
const KNOP = {
  akkoord: `${KNOP_BASIS} bg-green-700 text-white hover:bg-green-800`,
  gevaar: `${KNOP_BASIS} bg-red-700 text-white hover:bg-red-800`,
  secundair: `${KNOP_BASIS} border border-gray-300 bg-white text-navy-900 font-medium hover:border-gray-400`,
};

const dag = (yyyyMmDd: string, locale: string) => {
  const [j, m, d] = yyyyMmDd.split('-').map(Number);
  return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(j, m - 1, d));
};

/**
 * Indexatieslot (besluit 4, migratie 196): tariefvoorstellen van professionals
 * voor het nieuwe contractjaar, als actiepunten. Zonder akkoord vóór de
 * einddatum blijft het oude tarief staan. Rendert als <li>-items in de
 * actiepuntenlijst; niets als er geen voorstel wacht.
 */
export const TariefVoorstelActiepunten: React.FC = () => {
  const { data } = useQuery({
    queryKey: TARIEF_VOORSTELLEN_KEY,
    queryFn: tariefContractjaarApi.opdrachtgever,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const voorstellen = data?.voorstellen ?? [];
  if (voorstellen.length === 0) return null;
  return (
    <>
      {voorstellen.map((v) => (
        <VoorstelItem key={v.id} voorstel={v} kanBesluiten={!!data?.kan_besluiten} />
      ))}
    </>
  );
};

const VoorstelItem: React.FC<{ voorstel: TariefVoorstel; kanBesluiten: boolean }> = ({ voorstel: v, kanBesluiten }) => {
  const { t, i18n } = useTranslation('tariefContractjaar');
  const queryClient = useQueryClient();
  const locale = i18n.language?.startsWith('en') ? 'en-GB' : 'nl-NL';
  const [afwijzen, setAfwijzen] = useState(false);
  const [reden, setReden] = useState('');

  const besluit = useMutation({
    mutationFn: (akkoord: boolean) => tariefContractjaarApi.besluit(v.id, akkoord, akkoord ? null : reden.trim()),
    onSuccess: (_d, akkoord) => {
      toast.success(akkoord ? t('opdrachtgever.goedgekeurd') : t('opdrachtgever.afgewezen'));
      queryClient.invalidateQueries({ queryKey: TARIEF_VOORSTELLEN_KEY });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || t('fout')),
  });

  const nieuwEffectief = v.voorgesteld_tarief != null ? Math.round(v.voorgesteld_tarief * (1 - v.korting_pct / 100) * 100) / 100 : null;
  // Bij indexatie bevat de toelichting de CBS-bron (CPI alle huishoudens + maand).
  const soort =
    v.keuze === 'INDEXATIE'
      ? v.toelichting
        ? t('opdrachtgever.soort.INDEXATIE_BRON', { pct: v.indexatie_pct, bron: v.toelichting })
        : t('opdrachtgever.soort.INDEXATIE', { pct: v.indexatie_pct })
      : t('opdrachtgever.soort.EIGEN_VOORSTEL');

  return (
    <li className="rounded-xl border border-gray-200 border-l-4 border-l-amber-400 bg-white p-4 shadow-sm">
      <div className="flex items-start gap-3">
        <TrendingUp className="mt-0.5 h-6 w-6 flex-shrink-0 text-amber-600" />
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-amber-800">
              {t('opdrachtgever.badge', { dagen: v.dagen_resterend })}
            </span>
            <h2 className="text-sm font-semibold text-navy-900">{t('opdrachtgever.label')}</h2>
          </div>
          <p className="text-sm text-navy-700">
            {t('opdrachtgever.tekst', {
              naam: v.professional_naam,
              huidig: euro(v.huidig_tarief),
              nieuw: euro(v.voorgesteld_tarief),
              soort,
              datum: dag(v.contractjaar_eind, locale),
            })}
          </p>
          {v.korting_pct > 0 && (
            <p className="text-xs text-navy-500">{t('opdrachtgever.korting', { pct: v.korting_pct, nieuw: euro(nieuwEffectief) })}</p>
          )}
          {v.toelichting && <p className="rounded-lg bg-gray-50 p-2 text-sm italic text-navy-700">“{v.toelichting}”</p>}
          <p className="text-xs text-navy-500">{t('opdrachtgever.voetnoot')}</p>

          <div className="pt-1">
            {!kanBesluiten ? (
              <p className="text-xs text-navy-500">{t('opdrachtgever.alleenLezen')}</p>
            ) : afwijzen ? (
              <div className="space-y-2">
                <textarea
                  value={reden}
                  onChange={(e) => setReden(e.target.value)}
                  rows={2}
                  maxLength={1000}
                  placeholder={t('opdrachtgever.redenPlaceholder')}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
                <div className="flex flex-wrap gap-2">
                  <button type="button" className={KNOP.gevaar} disabled={besluit.isPending || !reden.trim()} onClick={() => besluit.mutate(false)}>
                    {t('opdrachtgever.jaAfwijzen')}
                  </button>
                  <button type="button" className={KNOP.secundair} onClick={() => setAfwijzen(false)}>
                    {t('annuleren')}
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                <button type="button" className={KNOP.akkoord} disabled={besluit.isPending} onClick={() => besluit.mutate(true)}>
                  <CheckCircle2 className="h-4 w-4" />
                  {t('opdrachtgever.akkoord')}
                </button>
                <button type="button" className={KNOP.secundair} disabled={besluit.isPending} onClick={() => setAfwijzen(true)}>
                  {t('opdrachtgever.afwijzen')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </li>
  );
};
